import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * YFI Country data interface matching the database schema
 */
export interface YfiCountry {
  id: string;
  country_code: string;
  country_name: string;
  region_code: "amdec" | "adc" | "apdc" | "edc";
  yfi_score: number;
  mental_health_score: number;
  workforce_score: number;
  entrepreneurship_score: number;
  classification: "Excellent" | "Good" | "Moderate" | "Deficient" | "Critical";
  data_availability: "Complete" | "Partial" | "Limited";
  member_count: number | null;
  flag_emoji: string | null;
  flag_code: string | null;
  created_at: string;
  updated_at: string;
  data_source: string | null;
  data_version: string | null;
}

/**
 * Mapping from database region codes to display names
 */
export const regionCodeToName: Record<string, string> = {
  amdec: "Africa and the Middle East",
  adc: "America",
  apdc: "Asia and the Pacific",
  edc: "Europe",
};

/**
 * Mapping from display names to database region codes
 */
export const regionNameToCode: Record<string, string> = {
  "Africa and the Middle East": "amdec",
  "America": "adc",
  "Americas": "adc",
  "Asia and the Pacific": "apdc",
  "Asia-Pacific": "apdc",
  "Europe": "edc",
};

interface UseYfiCountriesOptions {
  region?: string;
  classification?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch all YFI countries from the database
 */
export function useYfiCountries(options: UseYfiCountriesOptions = {}) {
  const { region, classification, enabled = true } = options;

  return useQuery({
    queryKey: ["yfi-countries", region, classification],
    queryFn: async (): Promise<YfiCountry[]> => {
      let query = supabase
        .from("yfi_countries")
        .select("*")
        .order("yfi_score", { ascending: false });

      if (region) {
        const regionCode = regionNameToCode[region] || region.toLowerCase();
        query = query.eq("region_code", regionCode as "amdec" | "adc" | "apdc" | "edc");
      }

      if (classification) {
        query = query.eq("classification", classification as "Excellent" | "Good" | "Moderate" | "Deficient" | "Critical");
      }

      const { data, error } = await query;

      if (error) {
        console.error("Failed to fetch YFI countries:", error);
        throw new Error("Failed to fetch YFI countries");
      }

      return (data || []) as YfiCountry[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

/**
 * Hook to fetch a single YFI country by country code
 */
export function useYfiCountry(countryCode: string | undefined) {
  return useQuery({
    queryKey: ["yfi-country", countryCode],
    queryFn: async (): Promise<YfiCountry | null> => {
      if (!countryCode) return null;

      const { data, error } = await supabase
        .from("yfi_countries")
        .select("*")
        .eq("country_code", countryCode.toLowerCase())
        .maybeSingle();

      if (error) {
        console.error("Failed to fetch YFI country:", error);
        throw new Error("Failed to fetch YFI country");
      }

      return data as YfiCountry | null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!countryCode,
  });
}

/**
 * Hook to get YFI regional statistics
 */
export function useYfiRegionalStats() {
  return useQuery({
    queryKey: ["yfi-regional-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("yfi_countries")
        .select("region_code, yfi_score, classification");

      if (error) {
        console.error("Failed to fetch YFI regional stats:", error);
        throw new Error("Failed to fetch regional stats");
      }

      // Calculate regional averages
      const regions: Record<string, { scores: number[]; classifications: Record<string, number> }> = {};
      
      for (const country of data || []) {
        const regionCode = country.region_code;
        if (!regions[regionCode]) {
          regions[regionCode] = { scores: [], classifications: {} };
        }
        regions[regionCode].scores.push(Number(country.yfi_score));
        regions[regionCode].classifications[country.classification] = 
          (regions[regionCode].classifications[country.classification] || 0) + 1;
      }

      return Object.entries(regions).map(([code, stats]) => ({
        region_code: code,
        region_name: regionCodeToName[code] || code,
        country_count: stats.scores.length,
        average_yfi: stats.scores.reduce((a, b) => a + b, 0) / stats.scores.length,
        min_yfi: Math.min(...stats.scores),
        max_yfi: Math.max(...stats.scores),
        classifications: stats.classifications,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get all countries utility - for synchronous access (with cache)
 * This is a compatibility layer for components that need synchronous data
 */
export function useAllYfiCountries() {
  const query = useYfiCountries();
  
  return {
    ...query,
    // Provide compatibility mappings
    countries: query.data || [],
    getCountryByCode: (code: string) => 
      query.data?.find(c => c.country_code === code.toLowerCase()),
    getCountryByName: (name: string) => 
      query.data?.find(c => c.country_name.toLowerCase() === name.toLowerCase()),
    countryFlagCodes: query.data?.reduce((acc, c) => {
      if (c.flag_code) {
        acc[c.country_name] = c.flag_code;
      }
      return acc;
    }, {} as Record<string, string>) || {},
  };
}
